import { scrollToElement } from 'acscp/components/common/helpers/scrollHelper';
import React from 'react';
import classNames from 'classnames';
import { AppState, BookingLeg, DateTime, ItineraryLeg as ItineraryLegModel } from 'acscp/store';
import { Row } from '../common/row';
import { ItineraryLegDetails } from './itineraryLegDetails';
import { Duration } from 'acscp/components/common/duration';
import style from './itineraryLeg.module.scss';
import { DateStyle, FormattedDate } from '../common/formattedDate';
import { addZeroWidthSpacesAroundSeparatorCharacters } from '../common/helpers/stringHelpers';
import { SplitLineFlightPlanTrack } from '../legs/flightPlan/splitLineFlightPlanTrack';
import { FormattedMessage } from 'react-intl';
import { LocaleCode } from 'acscp/translations';
import { connect } from 'react-redux';
import { DurationStyle } from '../common/helpers/formatMoments';

interface ItineraryLegProps {
    itineraryLeg?: ItineraryLegModel;
    leg: BookingLeg;
    legNumber: number;
    isFirstLeg: boolean;
    isLastLeg: boolean;
    showDetails: boolean;
    isLongStandingCustomer: boolean;

    toggleShowDetails: () => void;
}

interface InjectedStateProps {
    locale: LocaleCode;
}

export class ItineraryLegWithLocale extends React.Component<ItineraryLegProps &  InjectedStateProps, {}> {
    desktopButton: HTMLElement | null;
    mobileButton: HTMLElement | null;

    toggleDetails = () => {
        if (this.props.itineraryLeg) {
            this.props.toggleShowDetails();

            if (this.props.showDetails) {
                this.scrollToButton();
            }
        }
    }

    scrollToButton = () => {
        if (this.desktopButton && this.desktopButton.offsetTop) {
            scrollToElement(this.desktopButton);
        } else if (this.mobileButton && this.mobileButton.offsetTop) {
            scrollToElement(this.mobileButton);
        }
    }

    render(): JSX.Element {
        const {itineraryLeg, leg, isFirstLeg, isLastLeg, showDetails, locale, isLongStandingCustomer} = this.props;
        const isInPast = leg.arrivalDate.diff(DateTime.Now()) < 0;
        const classes = [style.LegSummaryWrapper];
        const mobileClasses = [style.MobileFlightPlan];
        if (isInPast) {
            classes.push(style.InPast);
            mobileClasses.push(style.InPast);
        }
        if (!itineraryLeg) {
            classes.push(style.Disabled);
            mobileClasses.push(style.Disabled);
        }
        const showText = <FormattedMessage id={'web.components.itinerary.itineraryLeg.View'} defaultMessage={'View'}/>;
        const hideText = <FormattedMessage id={'web.components.itinerary.itineraryLeg.Hide'} defaultMessage={'Hide'}/>;
        const conditionalShowOrHideText = (showDetails ? hideText : showText);

        return (
            <div className={style.PageBreakForPrinting}>
                <Row className={style.ItineraryLeg}>
                    <div className={classNames(mobileClasses)}>
                        <SplitLineFlightPlanTrack legs={[leg]}/>
                        <button
                            onClick={() => this.toggleDetails()}
                            disabled={!itineraryLeg}
                            className={!itineraryLeg ? style.Disabled : null}
                            ref={ref => this.mobileButton = ref}
                        >
                            {conditionalShowOrHideText}
                        </button>
                    </div>
                    <div className={classNames(classes)} onClick={() => this.toggleDetails()}>
                        <div className={classNames(style.SummaryItem, style.Label)}>
                            <FormattedMessage
                                id={'web.components.itinerary.itineraryLeg.Label'}
                                defaultMessage={'Leg {legNumber, number}'}
                                values={{legNumber: this.props.legNumber}}
                            />
                        </div>
                        <div className={classNames(style.SummaryItem, style.Depart)}>
                            {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                        </div>
                        <div className={classNames(style.SummaryItem, style.Arrive)}>
                            {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
                        </div>
                        <div className={classNames(style.SummaryItem, style.Date)}>
                            <FormattedDate date={leg.departureDate} style={DateStyle.Long} />
                        </div>
                        <div className={classNames(style.SummaryItem, style.DepartTime)}>
                            {leg.departureDate.getLocallyFormattedTime(locale)}
                        </div>
                        <div className={classNames(style.SummaryItem, style.ArriveTime)}>
                            {leg.arrivalDate.getLocallyFormattedTime(locale)}
                        </div>
                        <div className={classNames(style.SummaryItem, style.Duration)}>
                            <Duration duration={leg.flightTimeDuration} style={DurationStyle.Minimal} />
                        </div>
                        <div className={classNames(style.SummaryItem, style.Passengers)}>
                            {leg.numberOfPassengers}
                        </div>
                        <div className={style.ShowHideButtonWrapper}>
                            <button
                                ref={ref => this.desktopButton = ref}
                                disabled={!itineraryLeg}
                                className={!itineraryLeg ? style.Disabled : null}
                            >
                                {conditionalShowOrHideText}
                            </button>
                        </div>
                    </div>
                    {/* Toggle this using a class instead of removing it from the dom - this allows it to be expanded
                    automatically when printing */}
                    {itineraryLeg && <div
                        className={classNames({[style.ShowOnlyForPrint]: !showDetails},
                            style.DetailsPrint, style.ItinerarySeparator)}
                    >
                            <ItineraryLegDetails itineraryLeg={itineraryLeg} isLongStandingCustomer={isLongStandingCustomer}/>
                    </div>}
                    {!itineraryLeg && <div className={classNames(style.ShowOnlyForPrint, style.NoItinerary, style.ItinerarySeparator)}>
                        <FormattedMessage
                            id={'web.components.itineraryLeg.NoItinerary'}
                            defaultMessage={'Itinerary not available for this leg'}
                        />
                    </div>}
                </Row>
            </div>
        );
    }
}

function mapStateToProps(state: AppState): InjectedStateProps {
    return {
        locale: state.intl.locale
    };
}

export const ItineraryLeg = connect(mapStateToProps)(ItineraryLegWithLocale);
